import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { INLINE_PANEL_HEIGHT_PX, INLINE_PANEL_WIDTH_PX, } from '#pplx/frontend/composer-sdk/components/CommandPanel.js';
import { useKeyboardArbitrationCandidate } from '#pplx/frontend/composer-sdk/internal/keyboard/keyboardArbitration.js';
import { claimingProviders, haveSameProviders, registeredTriggers, selectProviders, } from '#pplx/frontend/composer-sdk/internal/typeahead/arbitration.js';
import { detectActiveTrigger } from '#pplx/frontend/composer-sdk/internal/typeahead/detectActiveTrigger.js';
import { makeEditorApi } from '#pplx/frontend/composer-sdk/internal/typeahead/editorApi.js';
import { hasTextAfterOffset } from '#pplx/frontend/composer-sdk/internal/typeahead/hasTextAfterOffset.js';
import { TypeaheadOverlay } from '#pplx/frontend/composer-sdk/internal/typeahead/TypeaheadOverlay.js';
import { TypeaheadResultsEngine } from '#pplx/frontend/composer-sdk/internal/typeahead/TypeaheadResultsEngine.js';
import { renderProviderSidePanel } from '#pplx/frontend/composer-sdk/internal/typeahead/typeaheadSidePanel.js';
import { TypedCommandMenuPlaceholder } from '#pplx/frontend/composer-sdk/internal/typeahead/TypedCommandMenuPlaceholder.js';
import { useInputAnchoredTypeahead } from '#pplx/frontend/composer-sdk/internal/typeahead/useInputAnchoredTypeahead.js';
import { useMarkerAnchoredCommandMenu } from '#pplx/frontend/composer-sdk/internal/typeahead/useMarkerAnchoredCommandMenu.js';
import { useTypeaheadActivation } from '#pplx/frontend/composer-sdk/internal/typeahead/useTypeaheadActivation.js';
import { moveTypeaheadHighlight, rowsOf, shouldPreselectFirstItem, useDispatchTypeaheadSelection, } from '#pplx/frontend/composer-sdk/internal/typeahead/useTypeaheadListNavigation.js';
import { useTypedTriggerLifecycle } from '#pplx/frontend/composer-sdk/internal/typeahead/useTypedTriggerLifecycle.js';
import { useComposer } from '#pplx/frontend/composer-sdk/useComposer.js';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalTypeaheadMenuPlugin, MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $getRoot, $getSelection, $isRangeSelection, $isTextNode, BLUR_COMMAND, COMMAND_PRIORITY_LOW, COMMAND_PRIORITY_NORMAL, SKIP_DOM_SELECTION_TAG, } from 'lexical';
import React, { useCallback, useEffect, useId, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import * as ReactDOM from 'react-dom';
// Margin kept between the inline popover and the viewport edge when shifting it to avoid a collision.
const VIEWPORT_PADDING_PX = 16;
/**
 * Drives the composer's providers through `TypeaheadResultsEngine`. Ordinary typeaheads use
 * `LexicalTypeaheadMenuPlugin` for trigger detection, caret anchoring, keyboard handling, and
 * node replacement. Persistent generic menus use one Composer-owned input-anchored path so their
 * overlay does not depend on Lexical's focus or selection-gated lifecycle. The controlled
 * command menu uses a third, marker-anchored path: an invisible editor node marks where the
 * query starts, so the menu opens programmatically with no typed trigger.
 */
export const TypeaheadPlugin = function TypeaheadPlugin({ typeaheadRef, providers, menuAlwaysOpen = false, disabled = false, wrapNavigation = true, overlayAnchorRef, commandMenuOverlay: { anchorRef: commandMenuAnchorRef, framed: commandMenuFramed }, onActiveVariantChange, overlayPlacement = 'below', commandMenu, }) {
    const { isActive } = useComposer();
    const [editor] = useLexicalComposerContext();
    const emptyState = commandMenu?.emptyState;
    const refreshStatus = commandMenu?.refreshStatus;
    const pendingContinuation = commandMenu?.pendingContinuation;
    const { allowEmptyGeneric, explicitProviders, genericProviders, keepOpen: shouldKeepOpen, inputAnchored: { engine: inputAnchoredEngine, provider: inputAnchoredProvider, providers: inputAnchoredProviders, providersRef: inputAnchoredProvidersRef, state: inputAnchoredState, }, lexical: { engine: lexicalEngine, providersRef: lexicalProvidersRef, reset: resetLexicalEngine, state: lexicalState, triggers: lexicalTriggers, }, } = useTypeaheadResultsEngine({
        menuAlwaysOpen,
        providers,
    });
    const [activePrefix, setActivePrefix] = useState(null);
    // The query the Lexical path is running, so display-only reads below can re-derive which
    // providers answer it. `null` whenever the Lexical path is closed.
    const [activeQueryState, setActiveQueryState] = useState(null);
    const activeQuery = activeQueryState?.query ?? null;
    const [lexicalDisplayedVariant, setLexicalDisplayedVariant] = useState(null);
    const [inputAnchoredDisplayedVariant, setInputAnchoredDisplayedVariant] = useState(null);
    const [markerDisplayedVariant, setMarkerDisplayedVariant] = useState(null);
    const { resolveProviders: resolveTypedTriggerProviders } = useTypedTriggerLifecycle({
        editor,
        providers,
    });
    const { isDismissed, clearDismissed, closeEmptyTypedQuery } = useSettledEmptyTypedQuery({
        editor,
        providers,
        activePrefix,
        activeQuery,
        lexicalProvidersRef,
    });
    const idBase = useId();
    const activePrefixRef = useRef(null);
    const explicitQueryRef = useRef(null);
    const previousProvidersRef = useRef(providers);
    // For an upward suggestion list, Lexical's automatic first-row selection is
    // display state, not proof that keyboard navigation has entered the list.
    const lexicalNavigationIndexRef = useRef(null);
    const moveLexicalHighlightRef = useRef(null);
    const commandMenuOpenRef = useRef(false);
    const prevCommandMenuOpenRef = useRef(false);
    const handoffCommandMenu = useCallback(() => {
        commandMenuOpenRef.current = false;
        prevCommandMenuOpenRef.current = false;
    }, []);
    const markerEngine = useManagedTypeaheadResultsEngine();
    const markerPath = useMarkerAnchoredCommandMenu({
        editor,
        providers,
        enabled: commandMenu?.open !== undefined,
        placeholder: commandMenu?.placeholder ?? '',
        placeholderVisibility: commandMenu?.placeholderVisibility ?? 'emptyQuery',
        engine: markerEngine.engine,
        state: markerEngine.state,
        reset: markerEngine.reset,
        onSelectionHandoff: handoffCommandMenu,
    });
    const markerActiveRef = markerPath.activeRef;
    const shouldMountLexicalMenu = isActive || activePrefix !== null;
    useTypeaheadActivation({ editor, enabled: shouldMountLexicalMenu, markerActiveRef });
    const triggerFn = useCallback((text) => {
        // Lexical publishes a final editor update during unmount. It must not start a new search.
        if (editor.getRootElement() === null)
            return null;
        if (disabled) {
            activePrefixRef.current = null;
            return null;
        }
        // Selection-only updates must not replace explicitly presented results with a typed run.
        if (explicitQueryRef.current !== null) {
            if ($getRoot().getTextContent() === explicitQueryRef.current)
                return null;
            explicitQueryRef.current = null;
        }
        // The marker-anchored menu owns the overlay while open; a trigger typed
        // into its query is query text, not a new menu.
        if (markerActiveRef.current) {
            activePrefixRef.current = null;
            return null;
        }
        const detected = detectActiveTrigger(text, lexicalTriggers, shouldKeepOpen ? false : allowEmptyGeneric);
        // A trigger whose providers all decline the query (`matchQuery`) reads as no trigger, so
        // Lexical never opens a menu (or takes the arrow/Enter keys) for a query nobody answers.
        // `onQueryChange` re-derives the same answering set: Lexical hands it only the query
        // string, and its snapshot in `lexicalProvidersRef` must be exactly these providers for
        // `row.providerIndex` and `onSelect` to resolve against them.
        if (detected === null) {
            activePrefixRef.current = null;
            return null;
        }
        const selected = resolveTypedTriggerProviders({
            prefix: detected.prefix,
            query: detected.query,
        });
        if (selected.length === 0) {
            activePrefixRef.current = null;
            return null;
        }
        if (isDismissed(detected, selected)) {
            activePrefixRef.current = null;
            return null;
        }
        clearDismissed();
        activePrefixRef.current = detected.prefix;
        return {
            leadOffset: detected.leadOffset,
            matchingString: detected.query,
            replaceableString: detected.prefix + detected.query,
        };
    }, [
        editor,
        markerActiveRef,
        lexicalTriggers,
        shouldKeepOpen,
        allowEmptyGeneric,
        resolveTypedTriggerProviders,
        isDismissed,
        clearDismissed,
        disabled,
    ]);
    const close = useCallback(() => {
        // Lexical may close its caret menu while the input-anchored explicit session owns the rows.
        if (explicitQueryRef.current !== null)
            return;
        // Persistent suggestions have their own engine, so closing the typed query leaves them open.
        activePrefixRef.current = null;
        moveLexicalHighlightRef.current = null;
        resetLexicalEngine();
        setActivePrefix(null);
        setActiveQueryState(null);
        lexicalProvidersRef.current = EMPTY_ARRAY;
    }, [lexicalProvidersRef, resetLexicalEngine]);
    useEffect(() => editor.registerCommand(BLUR_COMMAND, () => {
        const provider = lexicalProvidersRef.current[0];
        // Command menus leave dismissal to their caller, whose toggle may blur the editor.
        if (provider !== undefined && provider.display !== 'commandMenu') {
            close();
        }
        return false;
    }, COMMAND_PRIORITY_LOW), [editor, lexicalProvidersRef, close]);
    const onQueryChange = useCallback((query) => {
        const prefix = activePrefixRef.current;
        if (query === null || prefix === null) {
            close();
            return;
        }
        // Same answering set `triggerFn` checked; see the note there.
        const selected = claimingProviders(providers, prefix, query);
        if (selected.length === 0) {
            close();
            return;
        }
        // Lexical calls this inside editorState.read, including caret-only updates.
        const selection = $getSelection();
        // Match the typed trigger's text-node boundary, not text in later nodes.
        const anchor = $isRangeSelection(selection) && selection.isCollapsed() ? selection.anchor : null;
        const node = anchor?.getNode();
        const hasFollowingText = anchor !== null &&
            $isTextNode(node) &&
            hasTextAfterOffset(node.getTextContent(), anchor.offset);
        lexicalNavigationIndexRef.current = null;
        lexicalProvidersRef.current = selected;
        setActivePrefix(prefix);
        setActiveQueryState((previous) => previous?.query === query && previous.hasFollowingText === hasFollowingText
            ? previous
            : { query, hasFollowingText });
        lexicalEngine.run(query, selected);
    }, [lexicalEngine, lexicalProvidersRef, providers, close]);
    useEffect(() => {
        const previousProviders = previousProvidersRef.current;
        previousProvidersRef.current = providers;
        if (haveSameProviders(previousProviders, providers)) {
            return;
        }
        const prefix = activePrefixRef.current;
        if (explicitQueryRef.current !== null || prefix === null || activeQuery === null) {
            return;
        }
        // Show newly loaded results without waiting for the user to type another character.
        const selected = claimingProviders(providers, prefix, activeQuery);
        if (selected.length === 0) {
            close();
            return;
        }
        lexicalNavigationIndexRef.current = null;
        lexicalProvidersRef.current = selected;
        lexicalEngine.run(activeQuery, selected);
    }, [activeQuery, close, lexicalEngine, lexicalProvidersRef, providers]);
    const inputAnchoredPath = useInputAnchoredTypeahead({
        editor,
        providers: inputAnchoredProviders,
        explicitProviders,
        resolveTypedTriggerProviders,
        engine: inputAnchoredEngine,
        state: inputAnchoredState,
        allowEmptyGeneric,
        keepOpen: shouldKeepOpen,
        wrapNavigation,
        suspended: markerPath.active || disabled,
        activePrefixRef,
        selectedProvidersRef: inputAnchoredProvidersRef,
        explicitQueryRef,
        setActivePrefix,
        close,
    });
    const currentHandleRef = useRef(null);
    const { openSuggestions } = inputAnchoredPath;
    const controlledCommandMenuOpen = commandMenu?.open === true;
    useLayoutEffect(() => () => {
        // Clearing the consumer's ref alone would leave a captured handle able to call this engine.
        currentHandleRef.current = null;
    }, [controlledCommandMenuOpen, disabled, openSuggestions, typeaheadRef]);
    useImperativeHandle(typeaheadRef, () => {
        const handle = {
            openSuggestions: (request) => {
                // Controlled opening owns this commit even before its marker effect runs.
                if (currentHandleRef.current !== handle || disabled || controlledCommandMenuOpen) {
                    return false;
                }
                return openSuggestions(request);
            },
        };
        currentHandleRef.current = handle;
        return handle;
    }, [controlledCommandMenuOpen, disabled, openSuggestions]);
    // A normal empty query still uses the first provider that opts in. Persistent menus instead use
    // the first input-anchored generic provider because the Composer config is their sole opt-in.
    const displayedInputAnchoredProvider = inputAnchoredPath.active
        ? inputAnchoredProvidersRef.current[0]
        : shouldKeepOpen
            ? inputAnchoredProvider
            : genericProviders.find((provider) => provider.activateOnEmptyQuery);
    const inputAnchoredVariant = displayedInputAnchoredProvider?.display;
    // The Lexical path's providers for display-only reads: the active trigger's providers that
    // answer the active query, whose first member owns the variant, row count, and no-results copy.
    // Derived live from `providers`, not from the keystroke-old snapshot in `lexicalProvidersRef`:
    // the snapshot keeps `row.providerIndex` stable for selection, while reading live lets a
    // reconfigured provider re-size or re-label an already-open dropdown.
    const activeLexicalProviders = useMemo(() => activePrefix === null || activeQuery === null
        ? EMPTY_ARRAY
        : claimingProviders(providers, activePrefix, activeQuery), [providers, activePrefix, activeQuery]);
    // A results run can outlive Lexical's menu or render no empty affordance. Only mounted,
    // visible overlays can attach to the input's edge.
    const displayedVariant = useMemo(() => {
        if (markerPath.active) {
            return markerDisplayedVariant;
        }
        if (inputAnchoredPath.active) {
            return inputAnchoredDisplayedVariant;
        }
        return lexicalDisplayedVariant;
    }, [
        markerPath.active,
        markerDisplayedVariant,
        inputAnchoredPath.active,
        inputAnchoredDisplayedVariant,
        lexicalDisplayedVariant,
    ]);
    // Open across both paths (the marker and a typed trigger). Open ≠ displayed: a
    // run that errors in its first tick never displays, but must still report close.
    const commandMenuOpen = markerPath.active || (activePrefix !== null && displayedVariant === 'commandMenu');
    commandMenuOpenRef.current = commandMenuOpen;
    // Reports pre-paint, like the displayed-variant report below: an SDK-initiated
    // close (Escape, erasing the marker) unmounts the menu in this render, and the
    // consumer's trigger must flip in the same frame, not one painted frame later.
    const onCommandMenuOpenChange = commandMenu?.onOpenChange;
    // A replacement can remain open in the same commit that handoff retires the prior session.
    useLayoutEffect(() => {
        if (prevCommandMenuOpenRef.current === commandMenuOpen) {
            return;
        }
        prevCommandMenuOpenRef.current = commandMenuOpen;
        onCommandMenuOpenChange?.(commandMenuOpen);
    });
    // Composer unmounts this plugin with its last provider — before the report
    // above can flip to closed. Report the close on unmount, or the controlled
    // owner stays latched open and reopens the menu once a provider mounts again.
    const onCommandMenuOpenChangeRef = useRef(onCommandMenuOpenChange);
    onCommandMenuOpenChangeRef.current = onCommandMenuOpenChange;
    useLayoutEffect(() => {
        return () => {
            activePrefixRef.current = null;
            if (commandMenuOpenRef.current) {
                onCommandMenuOpenChangeRef.current?.(false);
            }
        };
    }, []);
    // Reconcile the controlled `open` with the menu's actual open state. Runs when
    // `open` changes and when a command-menu provider becomes available: a request
    // made before the provider mounts can't open anything, so it waits and retries
    // once one arrives instead of being dropped. An SDK-driven close reaches the
    // surface through `onOpenChange`, and acting on the surface's echoed `open`
    // here would race it (e.g. re-inserting a marker the user just erased).
    const requestedCommandMenuOpen = commandMenu?.open;
    const hasCommandMenuProvider = providers.some((provider) => provider.display === 'commandMenu');
    const { openMenu, closeMenu } = markerPath;
    useEffect(() => {
        if (disabled) {
            closeMenu();
            close();
        }
    }, [close, closeMenu, disabled]);
    useEffect(() => {
        if (requestedCommandMenuOpen === undefined ||
            requestedCommandMenuOpen === commandMenuOpenRef.current) {
            return;
        }
        if (requestedCommandMenuOpen) {
            if (hasCommandMenuProvider && !disabled) {
                openMenu();
            }
            return;
        }
        if (markerActiveRef.current) {
            closeMenu();
            return;
        }
        // A typed-trigger menu: resetting the engine hides the overlay while the
        // trigger text stays in the draft (typing again reopens it, like legacy).
        close();
    }, [
        requestedCommandMenuOpen,
        hasCommandMenuProvider,
        disabled,
        openMenu,
        closeMenu,
        close,
        markerActiveRef,
    ]);
    // The first answering provider owns menu presentation (see `activeLexicalProviders`).
    const activeMaxVisibleResults = activeLexicalProviders[0]?.maxVisibleResults;
    const activeEmptyMessage = activeLexicalProviders[0]?.emptyMessage;
    const activeHighlightStyle = activeLexicalProviders[0]?.highlightStyle;
    // Push the displayed variant before paint so the box's attached edge and its overlay become
    // visible in the same frame.
    useLayoutEffect(() => {
        onActiveVariantChange?.(displayedVariant);
    }, [displayedVariant, onActiveVariantChange]);
    // On unmount, clear the surface's variant so the box doesn't stay squared for an overlay
    // that no longer exists.
    useEffect(() => () => onActiveVariantChange?.(null), [onActiveVariantChange]);
    // Key each option by the row's stable identity, not its position: results re-fetch and
    // reorder as the user types, so an index key would track the wrong row across updates.
    // `item.id` is unique within a provider; `providerIndex` disambiguates a merged @ menu.
    const options = useMemo(() => rowsOf(lexicalState).map((row, flatIndex) => new TypeaheadMenuOption(`${idBase}-${row.providerIndex}-${row.item.id}`, row, flatIndex)), [lexicalState, idBase]);
    const lexicalSelectionContextsRef = useRef(new WeakMap());
    // Arrow arbitration runs outside Lexical's menu-render callback, so retain the latest options
    // for the registered candidate to navigate.
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const opensAbove = overlayPlacement === 'above';
    const handleEmptyOrPersistentArrowKey = (key) => inputAnchoredPath.onArrowKey(key, opensAbove && inputAnchoredVariant === 'suggestDropdown');
    const handleTypedTriggerArrowKey = (key) => {
        if (activePrefixRef.current === null)
            return false;
        const optionCount = optionsRef.current.length;
        const moveHighlight = moveLexicalHighlightRef.current;
        if (!optionCount || moveHighlight === null)
            return false;
        const provider = lexicalProvidersRef.current[0];
        return moveTypeaheadHighlight({
            editor,
            index: lexicalNavigationIndexRef.current,
            count: optionCount,
            key,
            wrap: wrapNavigation,
            above: opensAbove &&
                (provider?.display === 'suggestDropdown' ||
                    (provider?.display === 'commandMenu' && provider.navigation !== undefined)),
            onMove: moveHighlight,
            navigation: provider?.navigation,
        });
    };
    useKeyboardArbitrationCandidate({
        priority: 100,
        onKeyDown: (_event, key) => {
            const markerResult = markerPath.onArrowKey(key, opensAbove && markerPath.provider?.navigation !== undefined);
            if (markerResult !== false)
                return markerResult;
            const inputAnchoredResult = handleEmptyOrPersistentArrowKey(key);
            return inputAnchoredResult === false ? handleTypedTriggerArrowKey(key) : inputAnchoredResult;
        },
    });
    const onSelectOption = useDispatchTypeaheadSelection({
        editor,
        onSelection: (option, nodeToReplace, closeMenu) => {
            if (option.row.item.disabled === true)
                return null;
            const provider = lexicalProvidersRef.current[option.row.providerIndex];
            if (!provider)
                return { outcome: undefined, closeMenu, isCommandMenu: false };
            const context = lexicalSelectionContextsRef.current.get(option) ?? {
                selectionMethod: 'keyboard',
            };
            lexicalSelectionContextsRef.current.delete(option);
            const isCommandMenu = provider.display === 'commandMenu';
            return {
                closeMenu,
                isCommandMenu,
                outcome: provider.onSelect(option.row.item, makeEditorApi(editor, nodeToReplace), Object.freeze({ ...context, flatIndex: option.flatIndex })),
            };
        },
        onClose: ({ closeMenu }) => closeMenu(),
        onHandoff: ({ closeMenu, isCommandMenu }) => {
            if (isCommandMenu)
                handoffCommandMenu();
            closeMenu();
        },
    });
    const menuRenderFn = useCallback((anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
        // Keep Lexical's visual highlighter and the keyboard arbitration candidate synchronized.
        const moveHighlight = (index) => {
            lexicalNavigationIndexRef.current = index;
            setHighlightedIndex(index ?? -1);
        };
        moveLexicalHighlightRef.current = moveHighlight;
        if (explicitQueryRef.current !== null || lexicalState.status === 'idle') {
            return null;
        }
        if (activePrefix !== null && activePrefix !== '' && isSettledEmpty(lexicalState)) {
            return _jsx(CloseEmptyTypedQuery, { onClose: closeEmptyTypedQuery });
        }
        // The active trigger's first provider sets the variant (`display` is required). With no
        // matching provider there is nothing to show, so render nothing. Resolve against the run's
        // provider snapshot, so the row's `providerIndex` can't drift onto a different provider.
        const selectedProviders = lexicalProvidersRef.current;
        const variant = selectedProviders[0]?.display;
        if (!variant) {
            return null;
        }
        if (variant !== 'suggestDropdown' || !opensAbove) {
            lexicalNavigationIndexRef.current = selectedIndex;
        }
        // The inline popover follows the caret; a framed command menu stays in the frame.
        let target = overlayAnchorRef?.current;
        if (variant === 'inline') {
            target = anchorElementRef.current;
        }
        else if (variant === 'commandMenu') {
            target = commandMenuAnchorRef.current;
        }
        if (!target) {
            return null;
        }
        const onSelect = (index, source) => {
            const option = options[index];
            if (option) {
                lexicalSelectionContextsRef.current.set(option, {
                    selectionMethod: 'pointer',
                    ...(source === undefined ? {} : { source }),
                });
                selectOptionAndCleanUp(option);
            }
        };
        const controller = {
            selectedIndex,
            onHighlight: moveHighlight,
            onSelect,
        };
        if (variant === 'inline') {
            return renderDropdownOverlay({
                target,
                variant,
                inline: computeInlinePlacement(target),
                state: lexicalState,
                controller,
                emptyMessage: activeEmptyMessage,
                highlightStyle: activeHighlightStyle,
                onDisplayedVariantChange: setLexicalDisplayedVariant,
            });
        }
        if (variant === 'commandMenu') {
            return renderDropdownOverlay({
                target,
                variant,
                emptyState,
                refreshStatus,
                pendingContinuation,
                renderSidePanel: (state) => renderProviderSidePanel({
                    providers: selectedProviders,
                    selectedIndex,
                    state,
                    onSelect: ({ row, provider, flatIndex }) => {
                        // Ignore a saved panel action if the typed menu closed or its highlight, results
                        // or provider changed. Returning without selecting avoids choosing the wrong item.
                        if (activePrefixRef.current === null ||
                            lexicalNavigationIndexRef.current !== flatIndex ||
                            optionsRef.current[flatIndex]?.row !== row ||
                            lexicalProvidersRef.current[row.providerIndex] !== provider) {
                            return;
                        }
                        onSelect(flatIndex, 'sidePanel');
                    },
                }),
                placement: overlayPlacement,
                framed: commandMenuFramed,
                state: lexicalState,
                controller,
                emptyMessage: activeEmptyMessage,
                highlightStyle: activeHighlightStyle,
                onDisplayedVariantChange: setLexicalDisplayedVariant,
            });
        }
        return renderDropdownOverlay({
            target,
            variant,
            placement: overlayPlacement,
            maxVisibleResults: activeMaxVisibleResults,
            state: lexicalState,
            controller,
            emptyMessage: activeEmptyMessage,
            highlightStyle: activeHighlightStyle,
            onDisplayedVariantChange: setLexicalDisplayedVariant,
        });
    }, [
        lexicalState,
        activePrefix,
        closeEmptyTypedQuery,
        lexicalProvidersRef,
        options,
        overlayAnchorRef,
        commandMenuAnchorRef,
        commandMenuFramed,
        overlayPlacement,
        pendingContinuation,
        opensAbove,
        activeMaxVisibleResults,
        activeEmptyMessage,
        activeHighlightStyle,
        emptyState,
        refreshStatus,
    ]);
    // Empty-query suggestions render outside Lexical's resolution-gated menu. A persistent generic
    // menu stays on this input-anchored path, yielding only while a focused explicit trigger uses the
    // Lexical path.
    const inputAnchoredOverlay = inputAnchoredPath.active &&
        overlayAnchorRef?.current &&
        (inputAnchoredVariant === 'suggestDropdown' || inputAnchoredVariant === 'spotlight')
        ? renderDropdownOverlay({
            target: overlayAnchorRef.current,
            variant: inputAnchoredVariant,
            placement: inputAnchoredVariant === 'suggestDropdown' ? overlayPlacement : undefined,
            state: inputAnchoredState,
            maxVisibleResults: displayedInputAnchoredProvider?.maxVisibleResults,
            controller: inputAnchoredPath,
            emptyMessage: displayedInputAnchoredProvider?.emptyMessage,
            highlightStyle: displayedInputAnchoredProvider?.highlightStyle,
            onDisplayedVariantChange: setInputAnchoredDisplayedVariant,
        })
        : null;
    // The controlled command menu renders outside Lexical's menu as well: no typed
    // trigger exists, so its lifecycle hangs off the marker instead.
    const commandMenuTarget = commandMenuAnchorRef.current;
    const markerOverlay = markerPath.active && commandMenuTarget
        ? renderDropdownOverlay({
            target: commandMenuTarget,
            variant: 'commandMenu',
            emptyState,
            refreshStatus,
            pendingContinuation,
            framed: commandMenuFramed,
            renderSidePanel: (state) => renderProviderSidePanel({
                providers: markerPath.providersRef.current,
                selectedIndex: markerPath.selectedIndex,
                state,
                onSelect: markerPath.onSelectSidePanel,
            }),
            placement: overlayPlacement,
            state: markerEngine.state,
            controller: markerPath,
            emptyMessage: markerPath.provider?.emptyMessage,
            highlightStyle: markerPath.provider?.highlightStyle,
            onDisplayedVariantChange: setMarkerDisplayedVariant,
        })
        : null;
    const typedPlaceholder = getTypedCommandMenuPlaceholder({
        variant: lexicalDisplayedVariant,
        query: activeQuery,
        hasFollowingText: activeQueryState?.hasFollowingText ?? false,
        markerActive: markerPath.active,
        placeholder: commandMenu?.placeholder,
    });
    return (_jsxs(_Fragment, { children: [shouldMountLexicalMenu && (_jsx(LexicalTypeaheadMenuPlugin, { commandPriority: COMMAND_PRIORITY_NORMAL, options: options, triggerFn: triggerFn, onQueryChange: onQueryChange, onClose: close, onSelectOption: onSelectOption, menuRenderFn: menuRenderFn, preselectFirstItem: activePrefix !== '' &&
                    activePrefix !== null &&
                    shouldPreselectFirstItem(activeLexicalProviders[0]?.initialSelection) })), typedPlaceholder && _jsx(TypedCommandMenuPlaceholder, { placeholder: typedPlaceholder }), inputAnchoredOverlay, markerOverlay] }));
};
function isSameEmptyQuery(emptyQuery, query) {
    return (emptyQuery !== null &&
        emptyQuery.prefix === query.prefix &&
        emptyQuery.query === query.query &&
        haveSameProviders(emptyQuery.providers, query.providers));
}
function useSettledEmptyTypedQuery({ editor, providers, activePrefix, activeQuery, lexicalProvidersRef, }) {
    const emptyQueryRef = useRef(null);
    const isDismissed = useCallback((detected, selected) => isSameEmptyQuery(emptyQueryRef.current, {
        prefix: detected.prefix,
        query: detected.query,
        providers: selected,
    }), []);
    const clearDismissed = useCallback(() => {
        emptyQueryRef.current = null;
    }, []);
    const recheckTypedTrigger = useCallback(() => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection))
                selection.dirty = true;
        }, 
        // A pending search can settle after blur. Notify Lexical without taking DOM focus back.
        { tag: SKIP_DOM_SELECTION_TAG });
    }, [editor]);
    const closeEmptyTypedQuery = useCallback(() => {
        if (activePrefix === null || activeQuery === null)
            return;
        // Returning no match closes Lexical itself, not just the overlay, so its key handlers
        // release the editor. Remember this exact result through onClose's engine reset to avoid
        // reopening it on selection updates; edits or refreshed providers may match again.
        emptyQueryRef.current = {
            prefix: activePrefix,
            query: activeQuery,
            providers: lexicalProvidersRef.current,
        };
        recheckTypedTrigger();
    }, [activePrefix, activeQuery, lexicalProvidersRef, recheckTypedTrigger]);
    useEffect(() => {
        const emptyQuery = emptyQueryRef.current;
        if (emptyQuery !== null &&
            !haveSameProviders(emptyQuery.providers, claimingProviders(providers, emptyQuery.prefix, emptyQuery.query))) {
            clearDismissed();
            recheckTypedTrigger();
        }
    }, [providers, clearDismissed, recheckTypedTrigger]);
    return { isDismissed, clearDismissed, closeEmptyTypedQuery };
}
function CloseEmptyTypedQuery({ onClose }) {
    // Lexical opens its resolution in a transition. Close only after that menu mounts, or an
    // immediately empty synchronous result can close before the deferred open and leave keys trapped.
    useEffect(onClose, [onClose]);
    return null;
}
function isSettledEmpty(state) {
    switch (state.status) {
        case 'empty':
            // An empty page is not an exhausted search: retain continuation controls until all
            // pages settle. Loading covers pending promises, debounce and provider snapshots.
            return !state.providerSnapshots?.some((snapshot) => snapshot.continuation !== undefined);
        case 'idle':
        case 'loading':
        case 'done':
        case 'error':
            return false;
        default:
            return assertUnreachableValue(state);
    }
}
function getTypedCommandMenuPlaceholder({ variant, query, hasFollowingText, markerActive, placeholder, }) {
    switch (variant) {
        case 'commandMenu':
            return !markerActive && query === '' && !hasFollowingText ? placeholder : undefined;
        case 'inline':
        case 'suggestDropdown':
        case 'spotlight':
        case null:
            return undefined;
        default:
            return assertUnreachableValue(variant);
    }
}
function useManagedTypeaheadResultsEngine(config = {}) {
    const [state, setState] = useState({ status: 'idle' });
    const engine = useMemo(() => new TypeaheadResultsEngine({
        onStateChange: setState,
        retainPreviousRowsDuringLoading: config.retainPreviousRowsDuringLoading,
        retainPreviousRowsOnError: config.retainPreviousRowsOnError,
    }), [config.retainPreviousRowsDuringLoading, config.retainPreviousRowsOnError]);
    useEffect(() => () => engine.cancel(), [engine]);
    const reset = useCallback(() => {
        engine.cancel();
        setState({ status: 'idle' });
    }, [engine]);
    return { engine, reset, state };
}
function useTypeaheadResultsEngine({ menuAlwaysOpen, providers, }) {
    const triggers = useMemo(() => registeredTriggers(providers), [providers]);
    const explicitProviders = useMemo(() => providers.filter((provider) => provider.trigger !== ''), [providers]);
    const explicitTriggers = useMemo(() => registeredTriggers(explicitProviders), [explicitProviders]);
    const genericProviders = useMemo(() => selectProviders(providers, ''), [providers]);
    const allowEmptyGeneric = useMemo(() => genericProviders.some((provider) => provider.activateOnEmptyQuery), [genericProviders]);
    const persistentProviders = useMemo(() => genericProviders.filter((provider) => provider.display === 'suggestDropdown' || provider.display === 'spotlight'), [genericProviders]);
    const keepOpen = menuAlwaysOpen && persistentProviders.length > 0;
    const lexical = useManagedTypeaheadResultsEngine();
    const persistent = useManagedTypeaheadResultsEngine({
        retainPreviousRowsDuringLoading: true,
        retainPreviousRowsOnError: true,
    });
    // Each lifecycle keeps the provider snapshot its rows index into, so a persistent generic run
    // cannot overwrite the explicit Lexical path's selection ownership (or vice versa).
    const lexicalProvidersRef = useRef(EMPTY_ARRAY);
    const persistentProvidersRef = useRef(EMPTY_ARRAY);
    return {
        allowEmptyGeneric,
        explicitProviders,
        genericProviders,
        keepOpen,
        inputAnchored: {
            engine: keepOpen ? persistent.engine : lexical.engine,
            provider: persistentProviders[0],
            providers: keepOpen ? persistentProviders : providers,
            providersRef: keepOpen ? persistentProvidersRef : lexicalProvidersRef,
            state: keepOpen ? persistent.state : lexical.state,
        },
        lexical: {
            ...lexical,
            providersRef: lexicalProvidersRef,
            triggers: keepOpen ? explicitTriggers : triggers,
        },
    };
}
/** A menu option carrying its resolved row (item + section + owning provider index). */
class TypeaheadMenuOption extends MenuOption {
    row;
    flatIndex;
    constructor(key, row, flatIndex) {
        super(key);
        this.row = row;
        this.flatIndex = flatIndex;
    }
}
function renderDropdownOverlay({ controller, state, target, emptyMessage, ...variantProps }) {
    return ReactDOM.createPortal(_jsx(TypeaheadOverlay, { ...variantProps, state: state, selectedIndex: controller.selectedIndex, onHighlight: controller.onHighlight, onSelect: controller.onSelect, emptyMessage: emptyMessage }), target);
}
/**
 * Decide how the inline popover should sit relative to the caret anchor so it stays on
 * screen, given the panel's fixed width/height budget:
 *  - `isTop`: flip the popover above the caret when there isn't room for it below but there
 *    is above.
 *  - `viewportCollisionLeftOffsetPx`: shift it left when it would overflow the right edge,
 *    or right when it would overflow the left, keeping `VIEWPORT_PADDING_PX` of margin.
 */
function computeInlinePlacement(
/** The caret anchor element `LexicalTypeaheadMenuPlugin` positions the menu at. */
anchor) {
    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const isTop = spaceBelow < INLINE_PANEL_HEIGHT_PX && rect.top > INLINE_PANEL_HEIGHT_PX;
    let viewportCollisionLeftOffsetPx = 0;
    const rightEdge = rect.left + INLINE_PANEL_WIDTH_PX;
    if (rightEdge > window.innerWidth) {
        viewportCollisionLeftOffsetPx = window.innerWidth - rightEdge - VIEWPORT_PADDING_PX;
    }
    if (rect.left + viewportCollisionLeftOffsetPx < VIEWPORT_PADDING_PX) {
        viewportCollisionLeftOffsetPx = VIEWPORT_PADDING_PX - rect.left;
    }
    return { isTop, viewportCollisionLeftOffsetPx };
}
//# sourceMappingURL=TypeaheadPlugin.js.map